import { useCallback, useSyncExternalStore } from 'react'
import rawWhatsNewItems from '#app/data/whats-new.json'

export const WHATS_NEW_STORAGE_KEY = 'medisis:whats-new:read-ids'
export const WHATS_NEW_PREVIEW_MAX_LENGTH = 130

const EMPTY_SNAPSHOT = '[]'
const CHANGE_EVENT = 'medisis:whats-new-changed'

type WhatsNewBase = {
	id: string
	title: string
	preview: string
	publishedAt: string
}

export type WhatsNewInternalItem = WhatsNewBase & {
	kind: 'internal'
	body: string
}

export type WhatsNewExternalItem = WhatsNewBase & {
	kind: 'external'
	url: string
}

export type WhatsNewItem = WhatsNewInternalItem | WhatsNewExternalItem

export type WhatsNewItemWithRead = WhatsNewItem & { isRead: boolean }

export const whatsNewItems = [...(rawWhatsNewItems as WhatsNewItem[])].sort(
	(a, b) => b.publishedAt.localeCompare(a.publishedAt),
)

export function parseReadIds(raw: string | null): string[] {
	if (!raw) return []
	try {
		const parsed: unknown = JSON.parse(raw)
		if (!Array.isArray(parsed)) return []
		return parsed.filter(
			(id): id is string => typeof id === 'string' && id.length > 0,
		)
	} catch {
		return []
	}
}

export function serializeReadIds(ids: Iterable<string>): string {
	return JSON.stringify([...new Set(ids)])
}

export function addReadId(readIds: readonly string[], id: string): string[] {
	return readIds.includes(id) ? [...readIds] : [...readIds, id]
}

export function addAllReadIds(
	readIds: readonly string[],
	itemIds: readonly string[],
): string[] {
	return [...new Set([...readIds, ...itemIds])]
}

export function withReadState(
	items: readonly WhatsNewItem[],
	readIds: readonly string[],
): WhatsNewItemWithRead[] {
	const read = new Set(readIds)
	return items.map((item) => ({ ...item, isRead: read.has(item.id) }))
}

export function formatWhatsNewDate(publishedAt: string) {
	const [year, month, day] = publishedAt.split('-').map(Number)
	if (!year || !month || !day) return publishedAt
	return new Date(year, month - 1, day).toLocaleDateString('es-CR', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	})
}

export type WhatsNewInlineToken =
	| { type: 'text'; value: string }
	| { type: 'bold'; value: string }
	| { type: 'italic'; value: string }

/** WhatsApp-style: *bold*, **bold**, _italic_. Blank line starts a paragraph. */
export function parseWhatsNewInline(text: string): WhatsNewInlineToken[] {
	const tokens: WhatsNewInlineToken[] = []
	const markup = /\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_/g
	let lastIndex = 0
	for (const match of text.matchAll(markup)) {
		const index = match.index ?? 0
		if (index > lastIndex) {
			tokens.push({ type: 'text', value: text.slice(lastIndex, index) })
		}
		if (match[1] != null) {
			tokens.push({ type: 'bold', value: match[1] })
		} else if (match[2] != null) {
			tokens.push({ type: 'bold', value: match[2] })
		} else {
			tokens.push({ type: 'italic', value: match[3] ?? '' })
		}
		lastIndex = index + match[0].length
	}
	if (lastIndex < text.length) {
		tokens.push({ type: 'text', value: text.slice(lastIndex) })
	}
	return tokens.length > 0 ? tokens : [{ type: 'text', value: text }]
}

export function parseWhatsNewBody(body: string): WhatsNewInlineToken[][] {
	return body
		.split(/\n{2,}/)
		.filter((paragraph) => paragraph.length > 0)
		.map(parseWhatsNewInline)
}

function subscribe(onStoreChange: () => void) {
	function handleChange() {
		onStoreChange()
	}
	window.addEventListener('storage', handleChange)
	window.addEventListener(CHANGE_EVENT, handleChange)
	return () => {
		window.removeEventListener('storage', handleChange)
		window.removeEventListener(CHANGE_EVENT, handleChange)
	}
}

function getSnapshot() {
	try {
		return window.localStorage.getItem(WHATS_NEW_STORAGE_KEY) ?? EMPTY_SNAPSHOT
	} catch {
		return EMPTY_SNAPSHOT
	}
}

function getServerSnapshot() {
	return EMPTY_SNAPSHOT
}

function writeReadIds(ids: readonly string[]) {
	try {
		window.localStorage.setItem(WHATS_NEW_STORAGE_KEY, serializeReadIds(ids))
		window.dispatchEvent(new Event(CHANGE_EVENT))
	} catch {
		// ponytail: private mode / quota — read state stays in memory until reload
	}
}

function subscribeToIsClient() {
	return () => {}
}

export function useWhatsNew(items: readonly WhatsNewItem[] = whatsNewItems) {
	const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
	const isClient = useSyncExternalStore(
		subscribeToIsClient,
		() => true,
		() => false,
	)
	const readIds = parseReadIds(raw)
	const itemsWithRead = withReadState(items, readIds)
	const unreadCount = itemsWithRead.filter((item) => !item.isRead).length

	const markAsRead = useCallback(
		(id: string) => {
			writeReadIds(addReadId(readIds, id))
		},
		[readIds],
	)

	const markAllAsRead = useCallback(() => {
		writeReadIds(
			addAllReadIds(
				readIds,
				items.map((item) => item.id),
			),
		)
	}, [items, readIds])

	return {
		items: itemsWithRead,
		unreadCount: isClient ? unreadCount : 0,
		markAsRead,
		markAllAsRead,
	}
}
